import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { useGet } from "../../hooks/useGet";
import { MtApiResponse, MtData } from "../../utils/types";
import { useFilterContext } from "../../context/FilterContext";
import MtExportButton from "./MtExportButton";
import MtTablePagination from "./MtTablePagination";
import MtMessageDetailsModal from "./MtMessageDetailsModal";

const MtTable: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [selectedRow, setSelectedRow] = useState<MtData | null>(null);

  const {
    appliedNetwork,
    appliedPhoneNumber,
    appliedStartDate,
    appliedEndDate,
  } = useFilterContext();

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedNetwork) params.append("network", appliedNetwork);
    if (appliedPhoneNumber) params.append("msisdn", appliedPhoneNumber);
    if (appliedStartDate) params.append("startDate", appliedStartDate);
    if (appliedEndDate) params.append("endDate", appliedEndDate);
    params.append("page", (pageIndex + 1).toString());
    return params.toString();
  }, [
    appliedNetwork,
    appliedPhoneNumber,
    appliedStartDate,
    appliedEndDate,
    pageIndex,
  ]);

  const { data, isLoading, error } = useGet<MtApiResponse>(
    `/mt/v1/users?${queryParams}`,
    ["mt_table", queryParams]
  );

  const columns = useMemo<ColumnDef<MtData>[]>(
    () => [
      {
        accessorKey: "msisdn",
        header: "Phone Number",
        cell: (info) => (
          <div className="font-mono text-sm">{info.getValue() as string}</div>
        ),
      },
      {
        accessorKey: "mo",
        header: "Mobile Originated (MO)",
        cell: (info) => (
          <div
            className="truncate max-w-[200px]"
            title={info.getValue() as string}
          >
            <span className="bg-green-50 text-green-800 px-2 py-1 rounded text-xs font-medium">
              {info.getValue() as string}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "mt_response",
        header: "Mobile Terminated (MT) Response",
        cell: (info) => (
          <div
            className="truncate max-w-[300px]"
            title={info.getValue() as string}
          >
            <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded text-xs">
              {info.getValue() as string}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "network",
        header: "Network",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded text-xs font-semibold ${
              (info.getValue() as string) === "MTN"
                ? "bg-yellow-100 text-yellow-800"
                : (info.getValue() as string) === "Airtel"
                ? "bg-red-100 text-red-800"
                : (info.getValue() as string) === "Glo"
                ? "bg-green-100 text-green-800"
                : (info.getValue() as string) === "9Mobile"
                ? "bg-purple-100 text-purple-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {info.getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Timestamp",
        cell: (info) => (
          <div className="text-xs text-gray-600">
            {new Date(info.getValue() as string).toLocaleString()}
          </div>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: data?.last_page || 1,
    state: {
      pagination: {
        pageIndex,
        pageSize: 15,
      },
    },
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize: 15 })
          : updater;
      setPageIndex(newState.pageIndex);
    },
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading messages...</span>
      </div>
    );

  if (error)
    return (
      <div className="text-center py-8">
        <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-800">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-white uppercase tracking-wider"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No messages found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );

  return (
    <div className="p-4 sm:p-6 max-w-full sm:max-w-7xl mx-auto">
      <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
        {/* Export Button */}
        <div className="p-4 border-b border-gray-200">
          <MtExportButton data={data} />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-800">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 sm:px-6 py-3 text-left text-xs sm:text-sm font-semibold text-white uppercase tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No messages found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`transition-colors duration-150 cursor-pointer ${
                      index % 2 === 0 ? "bg-gray-50" : "bg-white"
                    } hover:bg-blue-50`}
                    onClick={() => setSelectedRow(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-800"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <MtTablePagination
          table={table}
          data={data}
          setPageIndex={setPageIndex}
        />
      </div>

      {/* Message Details Modal */}
      {selectedRow && (
        <MtMessageDetailsModal
          selectedRow={selectedRow}
          setSelectedRow={setSelectedRow}
        />
      )}
    </div>
  );
};

export default MtTable;
